import * as React from 'react';
import { toast } from 'react-toastify';
import { prepareErrorMessage } from './AsyncActionContainer';
import Loading from './Loading';
import ErrorMessage from './ErrorMessage';

export const useAsyncAction = (
	initialLoading = false,
	showToastForError = false
): [boolean, string, (action: () => Promise<void>) => Promise<string>] => {
	const [loading, setLoading] = React.useState(initialLoading);
	const [errorMessage, setErrorMessage] = React.useState<string>(null);

	// This fn cannot be called concurrently.
	// Returns the error message when the action failed, and null when it succeeded, so that callers can react to a
	// failure without having to prepare the message a second time themselves.
	const performAsyncAction = async (action: () => Promise<void>) => {
		setLoading(true);
		setErrorMessage(null);
		let failureMessage: string = null;
		try {
			await action();
		} catch (e) {
			failureMessage = prepareErrorMessage(e);
			setErrorMessage(failureMessage);
			// The state setter above does not update `errorMessage` until the next render, so the toast has to use
			// the message we just prepared rather than the value captured by this closure.
			if (showToastForError) toast(failureMessage, { bodyClassName: 'error' });
		} finally {
			setLoading(false);
		}
		return failureMessage;
	};

	return [loading, errorMessage, performAsyncAction];
};

/**
 * @deprecated this will often cause browser console errors as it is calling a hook (useEffect), and the intended usage
 * of this is calling asyncActionHandler outside the body of a functional component, e.g. within useEffect or within an
 * event handler.
 *
 * Replace with useAsyncAction
 */
export const useAsyncRender = (loadOnMount?: boolean) => {
	const [errorMessage, setErrorMessage] = React.useState(null as string);
	const [loading, setLoading] = React.useState(!!loadOnMount);
	const [actions, setActions] = React.useState(null as () => Promise<void>);

	React.useEffect(() => {
		const performActions = async () => {
			setLoading(true);
			try {
				await actions;
			} catch (e) {
				setErrorMessage(prepareErrorMessage(e));
			} finally {
				setLoading(false);
			}
		};
		if (actions) performActions();
	}, [actions]);

	const asyncActionHandler = (action: () => Promise<void>) => {
		setLoading(true);
		setActions(action);
	};

	let asyncRender = null;
	if (loading) asyncRender = <Loading />;
	else if (errorMessage) asyncRender = <ErrorMessage message={`Unexpected Error: ${errorMessage}`} />;

	return { asyncActionHandler, asyncRender };
};

// Attribution: https://usehooks.com/usePrevious/
export const usePrevious = <T,>(value: T) => {
	// The ref object is a generic container whose current property is mutable ...
	// ... and can hold any value, similar to an instance property on a class
	const ref = React.useRef<T>();
	// Store current value in ref
	React.useEffect(() => {
		ref.current = value;
	}, [value]); // Only re-run if value changes
	// Return previous value (happens before update in useEffect above)
	return ref.current;
};
